import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const isDashboard = location.pathname.startsWith('/app');
  if (isDashboard) return null;

  const links = [
    { to: '/#features', label: 'Features' },
    { to: '/#pricing', label: 'Pricing' },
    { to: '/compare', label: 'Compare' },
    { to: '/help', label: 'Help' },
    { to: '/#faq', label: 'FAQ' },
  ];

  const initials = (user?.name || user?.email || 'U').split(/[\s@]/).filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-zinc-900/80 bg-[#0a0a0b]/85 backdrop-blur">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full border border-[#d4a94a] flex items-center justify-center">
            <span className="font-display text-[#d4a94a] text-lg font-semibold leading-none">R</span>
          </div>
          <span className="font-display text-xl text-zinc-100">Reachly</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map(l => (
            <a key={l.to} href={l.to} className="text-sm text-zinc-400 hover:text-[#f5d67a] transition-colors">
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right side: signed-in chip vs. anonymous CTAs. When authenticated
            we surface the account initials + "Open app" so navigating to
            /help from inside the app doesn't feel like a session drop. */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={() => navigate('/app')} data-testid="nav-open-app">
                <LayoutDashboard size={14} className="mr-1.5"/>Open app
              </Button>
              <Link to="/app/settings" title={user.email} data-testid="nav-user-chip" className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-[#3a2f18] bg-[#1a1815] hover:border-[#d4a94a]">
                <div className="w-7 h-7 rounded-full bg-[#0a0a0b] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a] text-xs font-semibold">{initials || 'U'}</div>
                <span className="text-sm text-zinc-200 max-w-[160px] truncate">{user.name || user.email}</span>
              </Link>
            </>
          ) : (
            <>
              <Button variant="ghost" className="text-zinc-300 hover:text-[#f5d67a] hover:bg-transparent" onClick={() => navigate('/login')}>
                Log in
              </Button>
              <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={() => navigate('/signup')}>
                Start free
              </Button>
            </>
          )}
        </div>
        <button className="md:hidden text-zinc-300" onClick={() => setOpen(!open)}>
          <Menu size={22} />
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-zinc-900 bg-[#0a0a0b] px-5 py-4 flex flex-col gap-3">
          {links.map(l => (<a key={l.to} href={l.to} className="text-zinc-300" onClick={()=>setOpen(false)}>{l.label}</a>))}
          {user ? (
            <>
              <Button variant="outline" className="border-zinc-700 text-zinc-100" onClick={() => navigate('/app')}>Open app</Button>
              <div className="text-xs text-zinc-500">Signed in as {user.email}</div>
            </>
          ) : (
            <>
              <Button variant="outline" className="border-zinc-700 text-zinc-100" onClick={() => navigate('/login')}>Log in</Button>
              <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black" onClick={() => navigate('/signup')}>Start free</Button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
